// Structured source/provenance metadata shared by server and client.
//
// Sources are collected by tool execution (never scraped out of answer text)
// and transported to the browser as bounded message metadata. A source carries
// kind, title and canonical URL ONLY — never prompt text, never answer text.

import { ENTITY_KIND_LABELS } from "./routes";

export type SourceGroup = "KAfrequency Canon" | "Website" | "Current Web";

export interface AnswerSource {
  kind: string;
  title: string;
  url: string;
}

export interface AnswerMetadata {
  role?: string;
  tier?: string;
  sources?: AnswerSource[];
  webSearch?: boolean;
  /** Phase 4B.3 orchestration state — coarse, public-safe. */
  intent?: string;
  answerMode?: string;
  confidence?: "high" | "medium" | "low" | "none";
  retrieved?: boolean;
  contradiction?: boolean;
  grounding?: "verified" | "flagged" | "not_applicable";
  notice?: string | null;
  /** Phase 4D.3B — safe agent trace summary (never reasoning). */
  agent?: import("./orchestration/contract").PublicAgentTrace | null;
  /** Phase 4D.3B — confirmation-required action proposal. Never executed. */
  action?: import("./orchestration/contract").PublicActionProposal | null;
}



/** Governed grouping. Canon = first-party governed data. Web = external. */
const CANON_KINDS = new Set([
  "entity",
  "codex",
  "concept",
  "gate",
  "release",
  "music",
  "song",
  "book",
  "activation",
  "beat",
  "academy",
]);

export function sourceGroupFor(kind: string): SourceGroup {
  const k = String(kind ?? "").toLowerCase();
  if (k === "web") return "Current Web";
  if (CANON_KINDS.has(k)) return "KAfrequency Canon";
  return "Website";
}

export function sourceKindLabel(kind: string): string {
  return ENTITY_KIND_LABELS[String(kind ?? "").toLowerCase()] ?? "Source";
}

/** Max sources ever transported to the client. */
export const MAX_TRANSPORTED_SOURCES = 12;

/** Deduplicate by URL, cap the payload, and truncate titles. */
export function normalizeSources(
  list: AnswerSource[] | undefined | null,
  max = MAX_TRANSPORTED_SOURCES,
): AnswerSource[] {
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  const out: AnswerSource[] = [];
  for (const s of list) {
    const url = typeof s?.url === "string" ? s.url.trim() : "";
    if (!url || !/^https?:\/\//i.test(url)) continue;
    if (seen.has(url)) continue;
    seen.add(url);
    out.push({
      kind: String(s.kind ?? "page").toLowerCase().slice(0, 24),
      title: String(s.title ?? url).slice(0, 120),
      url: url.slice(0, 500),
    });
    if (out.length >= max) break;
  }
  return out;
}

export interface GroupedSources {
  group: SourceGroup;
  sources: AnswerSource[];
}

const GROUP_ORDER: SourceGroup[] = ["KAfrequency Canon", "Website", "Current Web"];

export function groupSources(list: AnswerSource[]): GroupedSources[] {
  const map = new Map<SourceGroup, AnswerSource[]>();
  for (const s of list) {
    const g = sourceGroupFor(s.kind);
    const bucket = map.get(g) ?? [];
    bucket.push(s);
    map.set(g, bucket);
  }
  return GROUP_ORDER.filter((g) => map.has(g)).map((group) => ({ group, sources: map.get(group)! }));
}
